'use client';

import type { FC } from 'react';
import { GiOpenChest, GiLockedChest, GiCrossedSwords, FaAtom, FaGift, FaArrowUp, FaStore, FaTrophy, FaDice, FaGavel, FaUserShield, FaCrosshairs, FaStar, FaBolt, FaQuestionCircle, IoTicket, RouletteDrum } from '../icons';
import type { IconType } from '../icons';
import {
  UserInventoryItemResponseSourceEnum,
  ItemSource,
} from '../../services/generated/Api';

type SourceType = UserInventoryItemResponseSourceEnum | ItemSource | string;

interface DropSourceIconProps {
  source: SourceType;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const getIconBySource = (source: SourceType): IconType => {
  switch (source) {
    case UserInventoryItemResponseSourceEnum.CASE_OPENING:
      return GiOpenChest;
    case UserInventoryItemResponseSourceEnum.CASE_BATTLE:
      return GiCrossedSwords;
    case UserInventoryItemResponseSourceEnum.CONTRACT:
      return FaAtom;
    case UserInventoryItemResponseSourceEnum.GIFT:
      return FaGift;
    case UserInventoryItemResponseSourceEnum.UPGRADE:
      return FaArrowUp;
    case UserInventoryItemResponseSourceEnum.AUCTION:
      return FaGavel;
    case UserInventoryItemResponseSourceEnum.FROM_SHOP:
      return FaStore;
    case UserInventoryItemResponseSourceEnum.DRAW:
      return FaDice;
    case UserInventoryItemResponseSourceEnum.CONTEST:
      return FaTrophy;
    case UserInventoryItemResponseSourceEnum.ADMIN_GRANT:
      return FaUserShield;
    case UserInventoryItemResponseSourceEnum.RUSH_MID:
    case UserInventoryItemResponseSourceEnum.ARENA:
      return FaCrosshairs;
    case UserInventoryItemResponseSourceEnum.DAILY_GIVEAWAY:
      return FaStar;
    case UserInventoryItemResponseSourceEnum.RUSH_RAFFLE:
    case UserInventoryItemResponseSourceEnum.BAND_RAFFLE:
      return IoTicket;
    case UserInventoryItemResponseSourceEnum.ROULETTE:
      return RouletteDrum;
    case UserInventoryItemResponseSourceEnum.BAND_CASE:
      return GiLockedChest;
    case UserInventoryItemResponseSourceEnum.BAND_RACE:
      return FaBolt;
    default:
      return FaQuestionCircle;
  }
};

const getColorBySource = (source: SourceType): string => {
  switch (source) {
    case UserInventoryItemResponseSourceEnum.CASE_OPENING:
      return '#f59e0b';
    case UserInventoryItemResponseSourceEnum.CASE_BATTLE:
      return '#ef4444';
    case UserInventoryItemResponseSourceEnum.CONTRACT:
      return '#8b5cf6';
    case UserInventoryItemResponseSourceEnum.GIFT:
      return '#ec4899';
    case UserInventoryItemResponseSourceEnum.UPGRADE:
      return '#10b981';
    case UserInventoryItemResponseSourceEnum.AUCTION:
      return '#3b82f6';
    case UserInventoryItemResponseSourceEnum.FROM_SHOP:
      return '#6366f1';
    case UserInventoryItemResponseSourceEnum.DRAW:
      return '#f97316';
    case UserInventoryItemResponseSourceEnum.CONTEST:
      return '#eab308';
    case UserInventoryItemResponseSourceEnum.ADMIN_GRANT:
      return '#94a3b8';
    case UserInventoryItemResponseSourceEnum.RUSH_MID:
    case UserInventoryItemResponseSourceEnum.ARENA:
    case UserInventoryItemResponseSourceEnum.RUSH_RAFFLE:
      return '#22d3ee';
    case UserInventoryItemResponseSourceEnum.DAILY_GIVEAWAY:
      return '#84cc16';
    case UserInventoryItemResponseSourceEnum.ROULETTE:
      return '#a78bfa';
    case UserInventoryItemResponseSourceEnum.BAND_CASE:
    case UserInventoryItemResponseSourceEnum.BAND_RAFFLE:
    case UserInventoryItemResponseSourceEnum.BAND_RACE:
      return '#ff4aa7';
    default:
      return '#64748b';
  }
};

const getSizeValue = (size: 'small' | 'medium' | 'large'): number => {
  switch (size) {
    case 'small':
      return 12;
    case 'medium':
      return 16;
    case 'large':
      return 20;
    default:
      return 16;
  }
};

export const DropSourceIcon: FC<DropSourceIconProps> = ({
  source,
  size = 'medium',
  className,
}) => {
  const Icon = getIconBySource(source);

  const color = getColorBySource(source);
  const fontSize = getSizeValue(size);

  return (
    <span
      className={`inline-flex items-center justify-center leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] ${className || ''}`}
      style={{
        color,
        fontSize: `${fontSize}px`,
      }}
    >
      <Icon />
    </span>
  );
};
